import { DateRangeType, NumberRangeValue, TypedFormFieldComponents } from '@fl/cmsch-fe-library';
import React, { FC, memo, useCallback } from 'react';
import { opt, Opt } from 'ts-opt';
import { CriteriaDefinition } from 'api/gen/CriteriaDefinition';
import { Criterias } from 'app/mating/types/criterias';
import { pairedCriteriaCodes } from 'app/mating/utils/paired-criteria-codes';
import { validateFormat, maxIsGreater } from 'utils/date-range-tests';
import { validateNumberRange, maxNumberIsGreater } from 'utils/number-range-tests';
import { validateBoundedNumberRange } from './number-range-validators';
export const milkProductionPhCriteriaCode = 'PHMlekoKg';
export const milkProductionPhMinimum = -3000;
export const milkProductionPhMaximum = 4000;
const dateRangeValidate = (value: DateRangeType): string | undefined => {
  const formatError = validateFormat(value);
  if (formatError) return formatError;
  return maxIsGreater(value);
};
const numberRangeValidate = (value: NumberRangeValue): string | undefined => {
  const maxSmallerError = maxNumberIsGreater(value);
  if (maxSmallerError) return maxSmallerError;
  return validateNumberRange(value);
};
const milkProductionRangeValidate = validateBoundedNumberRange(milkProductionPhMinimum, milkProductionPhMaximum);
const isMilkProductionPhCriteria = (criteriaDefinition: CriteriaDefinition): boolean => criteriaDefinition.code === milkProductionPhCriteriaCode;
interface Props {
  /** Current criteria definition to render. */
  criteriaDefinition: CriteriaDefinition;
  /** Disables the top-animal percentage selector. */
  disableTopAnimals?: boolean;
  /** Typed form field components for the active form. */
  Fields: TypedFormFieldComponents<Criterias, string>;
  /** Resets the selected criteria field. */
  resetCriteriaValue(criteriaName: Opt<string>): void;
}
const buildFieldName = (type: keyof Criterias, code: string): string => `[${type}][${code}]`;
const AccordionFieldBase: FC<Props> = props => {
  const {
    criteriaDefinition,
    disableTopAnimals,
    Fields,
    resetCriteriaValue
  } = props;
  const pairedCriteriaCode = pairedCriteriaCodes[criteriaDefinition.code];
  const resetPairedDateCriteria = useCallback((value: Opt<NumberRangeValue>): void => {
    if (pairedCriteriaCode && value.exists(x => x.selectedType === 'number')) {
      resetCriteriaValue(opt(pairedCriteriaCode));
    }
  }, [pairedCriteriaCode, resetCriteriaValue]);
  const resetPairedNumberCriteria = useCallback((value: Opt<DateRangeType>): void => {
    if (pairedCriteriaCode && value.exists(([dateFrom, dateTo]) => dateFrom !== null || dateTo !== null)) {
      resetCriteriaValue(opt(pairedCriteriaCode));
    }
  }, [pairedCriteriaCode, resetCriteriaValue]);
  if (criteriaDefinition.tag === 'BOOL') {
    return <Fields.YesNoButtonGroup name={buildFieldName('radios', criteriaDefinition.code)} label={criteriaDefinition.name} hideHelpInitially />;
  } else if (criteriaDefinition.tag === 'DATE') {
    return <Fields.DateRange name={buildFieldName('dateRanges', criteriaDefinition.code)} label={criteriaDefinition.name} validate={dateRangeValidate} onFieldChange={resetPairedNumberCriteria} hideHelpInitially clearable />;
  } else {
    const validate = isMilkProductionPhCriteria(criteriaDefinition) ? milkProductionRangeValidate : numberRangeValidate;
    return <Fields.NumberRange label={criteriaDefinition.name} name={buildFieldName('numberRanges', criteriaDefinition.code)} validate={validate} hideHelpInitially criteriaCode={criteriaDefinition.code} onResetCriteria={resetCriteriaValue} onFieldChange={resetPairedDateCriteria} disablePercentage={disableTopAnimals} cursorPointerOnDisabled />;
  }
};

/**
 * Memoized criteria field renderer for the accordion tree.
 *
 * @param props Criteria metadata and field bindings.
 * @returns The rendered criteria input.
 */
export const AccordionField = memo(AccordionFieldBase);